/**
 * Scope building + completeness (Phase 65 / BLU-594 PR3b).
 *
 * Turns a product's local slot assignments into the `startProductSyncJob` scope
 * shape, and decides whether a product is "ready to sync": every REQUIRED print
 * set (derived from its colours + the org's dark-set) must have a ready FRONT
 * source (back is optional). Only ready assignments (an uploaded upload, or any
 * drive/url) contribute a `SourceRef`.
 *
 * Phase 86 (D-11, D-21): a product whose type has Platen Size rules sends its
 * files as `platenSets` keyed `<platen>:<variant>` (never as plain `sets`),
 * built from its `PlatenLayout` snapshot. It is ready when every required set
 * has a ready front in AT LEAST ONE platen variant group; a group with no file
 * does not block (the run skips it and the missing-files dialog says so).
 *
 * Phase 86 slice B (86-28, R3-8): a BYO product's sets follow its variants'
 * own light, dark or medium (`requiredSets` with the variants' letters), so a
 * medium set is sent only when a variant reads Medium; its design code is the
 * product's own and its `variantSets` carry the operator's choices.
 */
import {
  DESIGN_SET_ORDER,
  DTG_PLATEN_CODES,
  resolveDesignSet,
  type DerivedDesignSet,
  type DtgPlatenCode,
  type EffectivePlatenPixels,
} from '@threadheads/shared';
import type {
  PlatenLayout,
  ProductStaging,
  SlotAssignment,
  DesignSet,
  DesignSide,
} from '../hooks/useStagedDesigns';
import { platenSlotKey, slotKey } from '../hooks/useStagedDesigns';
import type { SourceRef } from './apply-types';
import type { PlatenGroup } from './platen';

const SIDES = ['front', 'back'] as const;

type ScopeSets = Partial<Record<DesignSet, { front?: SourceRef; back?: SourceRef }>>;

/** A ready assignment yields a `SourceRef`; a staged/uploading/errored upload does not. */
export function readySourceRef(a: SlotAssignment | undefined): SourceRef | null {
  if (!a) return null;
  if (a.kind === 'upload') return a.state === 'uploaded' && a.key ? { type: 'upload', key: a.key } : null;
  if (a.kind === 'drive') return { type: 'drive', fileId: a.fileId };
  return { type: 'url', url: a.url };
}

/**
 * `requiredSets` lives in `@threadheads/shared` (Phase 86) so the backend,
 * the colour grid and this completeness check share one set split. Re-exported
 * here so every existing importer keeps working.
 */
export { requiredSets } from '@threadheads/shared';

/**
 * Complete = at least one required set, and every required set has a ready
 * FRONT source.
 *
 * `isCustomType` (the shared custom rule) short-circuits
 * to complete-with-NO-designs: an on-demand-custom product carries no catalog
 * design — its artwork arrives per-order via the line-item custom attribute —
 * so it is submittable as-is. The apply engine writes its `CUST-` SKU
 * metafield + projection and (when nothing was staged) simply has no print
 * files to materialize. Designs staged on it anyway are not sent:
 * `buildScopeProduct` gives it `sets: {}`. They stay staged, and are sent
 * again if the product goes back to ready-made.
 */
export function isProductComplete(staging: ProductStaging, sets: DesignSet[], isCustomType = false): boolean {
  if (isCustomType) return true;
  if (sets.length === 0) return false;
  return sets.every((set) => readySourceRef(staging[slotKey(set, 'front')]) !== null);
}

/** One `sets` object from READY slots, reading each slot through `keyFor`. */
function readySets(staging: ProductStaging, keyFor: (set: DesignSet, side: DesignSide) => string): ScopeSets {
  const out: ScopeSets = {};
  for (const set of DESIGN_SET_ORDER) {
    for (const side of SIDES) {
      const ref = readySourceRef(staging[keyFor(set, side) as keyof ProductStaging]);
      if (!ref) continue;
      const pair = out[set] ?? {};
      pair[side] = ref;
      out[set] = pair;
    }
  }
  return out;
}

/**
 * Build the scope `sets` object from all READY plain slot assignments (skips
 * not-yet-uploaded slots). Platen variant slots are never read here, so a
 * product without rules sends exactly today's shape.
 */
export function buildScopeSets(staging: ProductStaging): ScopeSets {
  return readySets(staging, slotKey);
}

/**
 * The scope `platenSets` for a ruled product: one entry per platen variant
 * group in its layout that has at least one READY file. Groups no longer in
 * the layout, plain slots and unready uploads are skipped. The server still
 * re-derives every size's group and refuses a stale scope.
 */
export function buildScopePlatenSets(staging: ProductStaging, layout: PlatenLayout): Record<string, ScopeSets> {
  const out: Record<string, ScopeSets> = {};
  for (const group of layout.groups) {
    const sets = readySets(staging, (set, side) => platenSlotKey(group.key, set, side));
    if (Object.keys(sets).length > 0) out[group.key] = sets;
  }
  return out;
}

/**
 * A ruled product is ready when every required set has a ready FRONT in at
 * least one platen variant group (D-11: missing groups do not block).
 */
export function isPlatenProductComplete(
  staging: ProductStaging,
  layout: PlatenLayout,
  sets: DesignSet[],
): boolean {
  if (sets.length === 0) return false;
  return sets.every((set) =>
    layout.groups.some((group) => readySourceRef(staging[platenSlotKey(group.key, set, 'front')]) !== null),
  );
}

/**
 * The layout snapshot for a ruled product, straight from 86-12's grouping:
 * each group's colours are mapped to print sets with the shared
 * `resolveDesignSet`, the same per-variant choice the apply engine makes.
 */
export function buildPlatenLayout(
  groups: readonly PlatenGroup[],
  productTypeName: string,
  colours: string[],
  darkColours: string[],
  /**
   * Phase 86 slice B: a BYO product's letter per SKU (the operator's choice,
   * else the metafield's, of the variant that SKU belongs to). Each SKU is
   * listed under its OWN set, so a colour whose sizes differ never lists a
   * size under a set it does not read. A SKU with no letter, or no lookup at
   * all: today's `resolveDesignSet`.
   */
  byoLetterOfSku?: (sku: string) => DerivedDesignSet | undefined,
  /** Round 4 (86-32): the rules org's Platen variants; a platen with a Small preset is listed in `smallPresetPlatens`. */
  pixels?: EffectivePlatenPixels | null,
): PlatenLayout {
  const presetPlatens = pixels
    ? DTG_PLATEN_CODES.filter(
        (platen) => pixels[platen]?.smallScaleBp != null && groups.some((g) => g.assignment.platen === platen),
      )
    : [];
  return {
    productTypeName,
    ...(presetPlatens.length > 0 ? { smallPresetPlatens: presetPlatens } : {}),
    groups: groups.map((group) => {
      const skusBySet: Partial<Record<DesignSet, string[]>> = {};
      for (const [colour, skus] of Object.entries(group.skusByColour)) {
        for (const sku of skus) {
          const set = resolveDesignSet(colour, colours, darkColours, byoLetterOfSku?.(sku));
          (skusBySet[set] ??= []).push(sku);
        }
      }
      return {
        key: group.key,
        platen: group.assignment.platen,
        platenVariant: group.assignment.platenVariant,
        sizes: [...group.sizes],
        skusBySet,
      };
    }),
  };
}

/** One product's entry in the `startProductSyncJob` scope. */
export interface ScopeProduct {
  productId: string;
  designCode: string;
  sets: ScopeSets;
  platenSets?: Record<string, ScopeSets>;
  /** Phase 86 slice B: a BYO product's operator choices (light, dark or medium per variant id). */
  variantSets?: Record<string, DerivedDesignSet>;
  /** Round 4 (R4-12): the platens this product uploads its Small by hand on (a ruled product only). */
  smallByHand?: DtgPlatenCode[];
}

/**
 * A ruled product (it has a `PlatenLayout`) sends `sets: {}` plus its
 * `platenSets`; any other product sends today's `sets` and no `platenSets`
 * key at all (D-21).
 * A custom product sends `sets: {}` and nothing else: its artwork comes with
 * each order, so a design staged while it was ready-made is not sent.
 */
export function buildScopeProduct(
  productId: string,
  designCode: string,
  staging: ProductStaging,
  layout: PlatenLayout | null,
  customProduct = false,
  /** Phase 86 slice B: sent only when the operator chose for some variant (no key otherwise). */
  variantSets?: Record<string, DerivedDesignSet>,
  /**
   * Round 4 (R4-12): the platens this product uploads its Small by hand on.
   * Sent (sorted, once each) only for a ruled product with at least one; it
   * can only stop a Small from being made, never make one.
   */
  smallByHand?: readonly DtgPlatenCode[],
): ScopeProduct {
  const choices = variantSets && Object.keys(variantSets).length > 0 ? { variantSets } : {};
  if (customProduct) return { productId, designCode, sets: {}, ...choices };
  if (layout) {
    const byHand = smallByHand ? DTG_PLATEN_CODES.filter((platen) => smallByHand.includes(platen)) : [];
    return {
      productId,
      designCode,
      sets: {},
      platenSets: buildScopePlatenSets(staging, layout),
      ...choices,
      ...(byHand.length > 0 ? { smallByHand: byHand } : {}),
    };
  }
  return { productId, designCode, sets: buildScopeSets(staging), ...choices };
}
